import React, { useMemo, useState } from 'react';
import {
  Badge,
  Box,
  Flex,
  SimpleGrid,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import {
  Buildings,
  CaretDown,
  CaretUp,
  Scales,
  TrendDown,
  TrendUp,
  UserMinus,
  UsersThree,
  WarningCircle,
} from 'phosphor-react';

import { GlassCard, GlassPageBg } from '../../../../components/glass';
import {
  IComparativoFranquia,
  useFinanceiroComparativoFranquias,
} from '../../../../hooks/useFinanceiro';
import { FinanceiroPageHeader } from '../components/FinanceiroPageHeader';
import { brl } from '../components/format';
import { InfoPopover } from '../components/InfoPopover';
import { KpiCard } from '../components/KpiCard';
import { useFinanceiroFilters } from '../components/useFinanceiroFilters';

const fmtPct = (v: number) => `${v.toFixed(1).replace('.', ',')}%`;

// Margem: vermelho se prejuízo, âmbar se < 10%, verde acima.
const corMargem = (margem: number, resultado: number) => {
  if (resultado < 0) return '#DC2626';
  if (margem < 10) return '#D97706';
  return '#16A34A';
};
// Pessoal/receita: benchmark ~25-35%. Acima pesa; verde até 35, âmbar 35-50, vermelho >50.
const corPessoal = (pct: number) => {
  if (pct > 50) return '#DC2626';
  if (pct > 35) return '#D97706';
  return '#16A34A';
};

type SortKey = keyof IComparativoFranquia;
const COLS: { key: SortKey; label: string; right?: boolean }[] = [
  { key: 'franquia', label: 'Unidade' },
  { key: 'receita', label: 'Receita', right: true },
  { key: 'resultado', label: 'Resultado', right: true },
  { key: 'margem', label: 'Margem', right: true },
  { key: 'vencido', label: 'Inadimplência', right: true },
  { key: 'pessoal_pct', label: 'Pessoal', right: true },
  { key: 'clientes_ativos', label: 'Clientes', right: true },
  { key: 'clientes_novos', label: 'Novos', right: true },
  { key: 'clientes_encerrados', label: 'Encerrados', right: true },
  { key: 'qtd', label: 'Lanç.', right: true },
];

const Destaque = ({
  label,
  unidade,
  valor,
  cor,
  icon,
}: {
  label: string;
  unidade: string;
  valor: string;
  cor: string;
  icon: React.ReactNode;
}) => (
  <GlassCard intensity="medium" p={4}>
    <Flex align="center" gap={2} mb={1}>
      <Box color={cor}>{icon}</Box>
      <Text
        fontSize="10px"
        fontWeight={700}
        color="#94A3B8"
        textTransform="uppercase"
        letterSpacing="0.05em"
      >
        {label}
      </Text>
    </Flex>
    <Text fontSize="md" fontWeight={700} color="#0F172A" noOfLines={1}>
      {unidade}
    </Text>
    <Text fontSize="sm" fontWeight={700} color={cor}>
      {valor}
    </Text>
  </GlassCard>
);

export const FinanceiroFranquiasPage = () => {
  const f = useFinanceiroFilters();
  // Tela de COMPARAÇÃO entre unidades: envia só o período, nunca a franquia —
  // o propósito é ver todas lado a lado (o filtro de franquia recorta as demais
  // telas do módulo, não esta). O backend só agrupa por franquia quando não vem
  // conta_origem.
  const { data, isError } = useFinanceiroComparativoFranquias({
    base: 'competencia',
    date_start: f.dateStart,
    date_end: f.dateEnd,
  });

  const itens = useMemo(() => data?.itens ?? [], [data]);
  const maxReceita = Math.max(1, ...itens.map(i => i.receita));

  const [sort, setSort] = useState<{ key: SortKey; dir: 'asc' | 'desc' }>({
    key: 'resultado',
    dir: 'desc',
  });
  const sorted = useMemo(() => {
    const arr = [...itens];
    arr.sort((a, b) => {
      if (sort.key === 'franquia') {
        const c = (a.franquia || '').localeCompare(b.franquia || '');
        return sort.dir === 'asc' ? c : -c;
      }
      const av = a[sort.key] as number;
      const bv = b[sort.key] as number;
      return sort.dir === 'asc' ? av - bv : bv - av;
    });
    return arr;
  }, [itens, sort]);

  const clickSort = (key: SortKey) =>
    setSort(s =>
      s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'desc' },
    );

  // Destaques: melhor margem, maior inadimplência, pessoal mais pesado.
  const destaques = useMemo(() => {
    if (itens.length === 0) return null;
    const comReceita = itens.filter(i => i.receita > 0);
    const melhorMargem = [...(comReceita.length ? comReceita : itens)].sort(
      (a, b) => b.margem - a.margem,
    )[0];
    const maiorInad = [...itens].sort((a, b) => b.vencido - a.vencido)[0];
    const maiorPessoal = [...(comReceita.length ? comReceita : itens)].sort(
      (a, b) => b.pessoal_pct - a.pessoal_pct,
    )[0];
    const maiorOnboarding = [...itens].sort((a, b) => b.clientes_novos - a.clientes_novos)[0];
    const maiorSaida = [...itens].sort((a, b) => b.clientes_encerrados - a.clientes_encerrados)[0];
    return { melhorMargem, maiorInad, maiorPessoal, maiorOnboarding, maiorSaida };
  }, [itens]);

  return (
    <GlassPageBg>
      <Box maxW="1320px" mx="auto" w="full" px={{ base: 4, md: 8 }} py={{ base: 6, md: 10 }}>
        <FinanceiroPageHeader
          eyebrow="Franquias"
          titulo="Comparativo de unidades"
          subtitulo="Rentabilidade, inadimplência, pessoal e movimento de clientes — cada base conta uma vez"
          icon={<Buildings size={24} weight="fill" />}
          cor="#0891B2"
          info={
            <>
              <Text>
                Compara as <b>unidades</b> (conta_origem) no período: receita, resultado, margem,
                <b> inadimplência</b> (a receber vencido) e <b>custo de pessoal</b> (% da receita,
                ideal 25–35%). Clique no cabeçalho da tabela para ordenar.
              </Text>
              <Text mt={2}>
                <b>Clientes</b> = base ativa; <b>Novos</b> = criados no período (onboarding);{' '}
                <b>Encerrados</b> = contratos com termo encerrado no período (offboarding, via
                contratos do Conta Azul).
              </Text>
            </>
          }
        />

        {isError && (
          <GlassCard intensity="medium" p={6} mb={5} tint="#DC2626">
            <Text color="#71717A">Não foi possível carregar o comparativo.</Text>
          </GlassCard>
        )}

        <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4} mb={5}>
          <KpiCard
            label="Receita (consolidada)"
            valor={data?.total_receita ?? 0}
            color="#16A34A"
            icon={<TrendUp size={20} weight="duotone" />}
            index={0}
            info={
              <InfoPopover title="Receita consolidada">
                Soma da receita de <b>todas as unidades</b>. Cada base entra uma única vez.
              </InfoPopover>
            }
          />
          <KpiCard
            label="Resultado"
            valor={data?.total_resultado ?? 0}
            color={(data?.total_resultado ?? 0) >= 0 ? '#16A34A' : '#DC2626'}
            icon={<Scales size={20} weight="duotone" />}
            index={1}
            info={
              <InfoPopover title="Resultado consolidado">
                Receita − Despesa do grupo inteiro no período.
              </InfoPopover>
            }
          />
          <KpiCard
            label="Inadimplência"
            valor={data?.total_vencido ?? 0}
            color="#D97706"
            icon={<WarningCircle size={20} weight="duotone" />}
            index={2}
            info={
              <InfoPopover title="Inadimplência consolidada">
                A receber <b>vencido</b> (status ATRASADO) somado de todas as unidades.
              </InfoPopover>
            }
          />
          <KpiCard
            label="Custo de pessoal"
            valor={data?.total_pessoal ?? 0}
            color="#DB2777"
            icon={<TrendDown size={20} weight="duotone" />}
            index={3}
            info={
              <InfoPopover title="Custo de pessoal">
                Folha/encargos/benefícios/sócios somados. Por unidade, a coluna <b>Pessoal</b>{' '}
                mostra quanto isso pesa na receita (ideal 25–35%).
              </InfoPopover>
            }
          />
        </SimpleGrid>

        {destaques && (
          <SimpleGrid columns={{ base: 2, md: 5 }} spacing={4} mb={5}>
            <Destaque
              label="Melhor margem"
              unidade={destaques.melhorMargem.franquia}
              valor={fmtPct(destaques.melhorMargem.margem)}
              cor="#16A34A"
              icon={<TrendUp size={16} weight="bold" />}
            />
            <Destaque
              label="Maior inadimplência"
              unidade={destaques.maiorInad.franquia}
              valor={brl(destaques.maiorInad.vencido)}
              cor="#D97706"
              icon={<WarningCircle size={16} weight="bold" />}
            />
            <Destaque
              label="Pessoal mais pesado"
              unidade={destaques.maiorPessoal.franquia}
              valor={`${fmtPct(destaques.maiorPessoal.pessoal_pct)} da receita`}
              cor="#DB2777"
              icon={<TrendDown size={16} weight="bold" />}
            />
            <Destaque
              label="Mais clientes novos"
              unidade={destaques.maiorOnboarding.franquia}
              valor={`+${destaques.maiorOnboarding.clientes_novos} no período`}
              cor="#2563EB"
              icon={<UsersThree size={16} weight="bold" />}
            />
            <Destaque
              label="Mais saídas"
              unidade={destaques.maiorSaida.franquia}
              valor={`−${destaques.maiorSaida.clientes_encerrados} no período`}
              cor="#DC2626"
              icon={<UserMinus size={16} weight="bold" />}
            />
          </SimpleGrid>
        )}

        {itens.length === 0 ? (
          <GlassCard intensity="medium" p={10} textAlign="center">
            <Buildings size={36} color="#94A3B8" weight="duotone" />
            <Text color="#71717A" mt={3}>
              Sem unidades no período. Os dados aparecem quando as bases do Conta Azul forem
              carregadas (cada uma marcada por <b>conta_origem</b>).
            </Text>
          </GlassCard>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35 }}
          >
            <GlassCard intensity="strong" p={{ base: 1, md: 2 }}>
              <TableContainer>
                <Table variant="simple" size="sm">
                  <Thead>
                    <Tr>
                      {COLS.map(col => {
                        const active = sort.key === col.key;
                        return (
                          <Th
                            key={col.key}
                            cursor="pointer"
                            onClick={() => clickSort(col.key)}
                            textAlign={col.right ? 'right' : 'left'}
                            color={active ? '#0891B2' : '#94A3B8'}
                            userSelect="none"
                            borderColor="rgba(15,23,42,0.06)"
                            whiteSpace="nowrap"
                          >
                            <Flex
                              align="center"
                              gap={1}
                              justify={col.right ? 'flex-end' : 'flex-start'}
                            >
                              {col.label}
                              {active &&
                                (sort.dir === 'desc' ? (
                                  <CaretDown size={10} weight="bold" />
                                ) : (
                                  <CaretUp size={10} weight="bold" />
                                ))}
                            </Flex>
                          </Th>
                        );
                      })}
                    </Tr>
                  </Thead>
                  <Tbody>
                    {sorted.map(fr => {
                      const margemCor = corMargem(fr.margem, fr.resultado);
                      const inadPct = fr.receita > 0 ? (fr.vencido / fr.receita) * 100 : 0;
                      const recW = Math.max(2, (fr.receita / maxReceita) * 100);
                      return (
                        <Tr key={fr.franquia} _hover={{ bg: 'rgba(8,145,178,0.04)' }}>
                          <Td borderColor="rgba(15,23,42,0.05)" maxW="220px">
                            <Flex align="center" gap={2} minW={0}>
                              <Buildings size={15} color="#0891B2" weight="duotone" />
                              <Text fontWeight={600} color="#0F172A" noOfLines={1} fontSize="sm">
                                {fr.franquia}
                              </Text>
                            </Flex>
                          </Td>
                          <Td borderColor="rgba(15,23,42,0.05)" isNumeric>
                            <Text
                              fontWeight={600}
                              color="#0F172A"
                              sx={{ fontVariantNumeric: 'tabular-nums' }}
                            >
                              {brl(fr.receita)}
                            </Text>
                            <Box
                              mt={1}
                              h="3px"
                              bg="rgba(15,23,42,0.05)"
                              borderRadius="full"
                              overflow="hidden"
                            >
                              <Box h="full" w={`${recW}%`} bg="#16A34A" borderRadius="full" />
                            </Box>
                          </Td>
                          <Td
                            borderColor="rgba(15,23,42,0.05)"
                            isNumeric
                            color={fr.resultado >= 0 ? '#16A34A' : '#DC2626'}
                            fontWeight={700}
                            sx={{ fontVariantNumeric: 'tabular-nums' }}
                          >
                            {brl(fr.resultado)}
                          </Td>
                          <Td borderColor="rgba(15,23,42,0.05)" isNumeric>
                            {fr.receita > 0 ? (
                              <Badge
                                bg={`${margemCor}1A`}
                                color={margemCor}
                                borderRadius="full"
                                px={2}
                                fontSize="11px"
                              >
                                {fmtPct(fr.margem)}
                              </Badge>
                            ) : (
                              <Text color="#94A3B8" fontSize="sm">
                                —
                              </Text>
                            )}
                          </Td>
                          <Td borderColor="rgba(15,23,42,0.05)" isNumeric>
                            <Text
                              color={fr.vencido > 0 ? '#D97706' : '#94A3B8'}
                              fontWeight={fr.vencido > 0 ? 600 : 400}
                              sx={{ fontVariantNumeric: 'tabular-nums' }}
                            >
                              {brl(fr.vencido)}
                            </Text>
                            {fr.vencido > 0 && (
                              <Text fontSize="10px" color="#94A3B8">
                                {fmtPct(inadPct)} da receita
                              </Text>
                            )}
                          </Td>
                          <Td borderColor="rgba(15,23,42,0.05)" isNumeric>
                            {fr.receita > 0 ? (
                              <Text
                                fontWeight={600}
                                color={corPessoal(fr.pessoal_pct)}
                                sx={{ fontVariantNumeric: 'tabular-nums' }}
                              >
                                {fmtPct(fr.pessoal_pct)}
                              </Text>
                            ) : (
                              <Text color="#94A3B8" fontSize="sm">
                                —
                              </Text>
                            )}
                            {fr.pessoal > 0 && (
                              <Text fontSize="10px" color="#94A3B8">
                                {brl(fr.pessoal)}
                              </Text>
                            )}
                          </Td>
                          <Td
                            borderColor="rgba(15,23,42,0.05)"
                            isNumeric
                            fontWeight={600}
                            color="#0F172A"
                            sx={{ fontVariantNumeric: 'tabular-nums' }}
                          >
                            {fr.clientes_ativos}
                          </Td>
                          <Td borderColor="rgba(15,23,42,0.05)" isNumeric>
                            {fr.clientes_novos > 0 ? (
                              <Badge
                                bg="#E7F7EE"
                                color="#16A34A"
                                borderRadius="full"
                                px={2}
                                fontSize="11px"
                              >
                                +{fr.clientes_novos}
                              </Badge>
                            ) : (
                              <Text color="#94A3B8" fontSize="sm">
                                —
                              </Text>
                            )}
                          </Td>
                          <Td borderColor="rgba(15,23,42,0.05)" isNumeric>
                            {fr.clientes_encerrados > 0 ? (
                              <Badge
                                bg="#FEE2E2"
                                color="#DC2626"
                                borderRadius="full"
                                px={2}
                                fontSize="11px"
                              >
                                −{fr.clientes_encerrados}
                              </Badge>
                            ) : (
                              <Text color="#94A3B8" fontSize="sm">
                                —
                              </Text>
                            )}
                          </Td>
                          <Td
                            borderColor="rgba(15,23,42,0.05)"
                            isNumeric
                            color="#94A3B8"
                            fontSize="xs"
                          >
                            {fr.qtd}
                          </Td>
                        </Tr>
                      );
                    })}
                  </Tbody>
                </Table>
              </TableContainer>
            </GlassCard>
          </motion.div>
        )}
      </Box>
    </GlassPageBg>
  );
};
